import { useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { Loader2, Eye, EyeOff } from 'lucide-react'
import { Button } from './core/Button'
import enttroLogo from '../assets/logo.png'
import { DEFAULT_ENDPOINT_URL_EXPORT } from '../graphql/client'

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const token = searchParams.get('token')
  const email = searchParams.get('email')

  const [password, setPassword] = useState('')
  const [passwordConfirm, setPasswordConfirm] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (password.length < 5) {
      setError('Passwort muss mindestens 5 Zeichen lang sein.')
      return
    }

    if (password !== passwordConfirm) {
      setError('Passwörter stimmen nicht überein.')
      return
    }

    try {
      setLoading(true)

      const response = await fetch(`${DEFAULT_ENDPOINT_URL_EXPORT}/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          token,
          email,
          password,
          password_confirmation: passwordConfirm,
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.message || 'Reset fehlgeschlagen')
      }

      setSuccess(true)

      setTimeout(() => {
        navigate('/')
      }, 3000)

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Serverfehler')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a2540] via-[#0d3a5f] to-[#0a2540] flex items-center justify-center p-4">

      <div className="w-full max-w-[420px] bg-white rounded-[16px] shadow-2xl p-8 border border-gray-100">

        <div className="text-center mb-6">
          <img src={enttroLogo} alt="ENTRO" className="h-14 mx-auto mb-4" />
          <h2 className="text-[22px] font-medium">Neues Passwort setzen</h2>
        </div>

        {success ? (
          <div className="text-center text-green-600">
            Passwort erfolgreich geändert.  
            Sie werden zur Anmeldung weitergeleitet.
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">

            {error && (
              <div className="text-red-600 text-[14px] bg-red-50 p-3 rounded-lg">
                {error}
              </div>
            )}

            <div>
              <label className="text-[14px] font-medium mb-2 block">
                Neues Passwort
              </label>

              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full box-border px-4 py-3 border border-[#e9e9e9] rounded-[10px]"
                />

                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div>
              <label className="text-[14px] font-medium mb-2 block">
                Passwort bestätigen
              </label>

              <input
                type="password"
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                className="w-full box-border px-4 py-3 border border-[#e9e9e9] rounded-[10px]"
              />
            </div>

            <Button
              type="submit"
              fullWidth
              size="lg"
              disabled={loading}
              icon={loading ? <Loader2 className="animate-spin" /> : undefined}
            >
              {loading ? 'Wird gespeichert...' : 'Passwort ändern'}
            </Button>

          </form>
        )}

      </div>

    </div>
  )
}